/** New-voter inclusion application (Form-11, rule 32(2)) — data helpers. */

import {
    displayField,
    formatDateField,
    isValidBdPhone,
    normalizeBdPhone,
    normalizeDigits,
    parseDateField,
    toBengaliDigits,
} from '../voter-migration-form/voterMigrationFormEngine';

export {
    displayField,
    formatDateField,
    isValidBdPhone,
    normalizeBdPhone,
    normalizeDigits,
    parseDateField,
    toBengaliDigits,
};

/** The five documents ৭ lets the applicant tick, in the order the form prints them. */
export const PROOF_KEYS = ['proofWatchman', 'proofHoldingTax', 'proofRent', 'proofUtility', 'proofOther'];

/** Voters are counted on 1 January of the year, so the form asks for that age. */
export const currentRollYear = () => toBengaliDigits(new Date().getFullYear());

export const INITIAL_VOTER_INCLUSION_FORM = {
    // প্রাপক — রেজিস্ট্রেশন অফিসার
    officeUpazila: '',
    officeDistrict: '',
    // ১-৪
    applicantName: '',
    fatherName: '',
    motherName: '',
    spouseName: '',
    // ৫ — ঠিকানা
    village: '',
    road: '',
    houseNo: '',
    postOffice: '',
    ward: '',
    union: '',
    thana: '',
    district: '',
    division: '',
    // ৬-৭
    notIncludedReason: '',
    stayYears: '',
    stayMonths: '',
    proofWatchman: '',
    proofHoldingTax: '',
    proofRent: '',
    proofUtility: '',
    proofOther: '',
    // ৮(গ) — age is worked out from these two
    birthDate: '',
    ageYear: currentRollYear(),
    // তারিখ / ক্ষমতাপত্র
    applicationDate: '',
    representativeName: '',
    representativeFather: '',
    authorizationDate: '',
    representativeAddress: '',
    // অঙ্গীকারনামা
    mobile: '',
};

/** Left blank without it being a mistake: the ক্ষমতাপত্র, ticks and open lines. */
const OPTIONAL_KEYS = new Set([
    'spouseName',
    'road',
    'houseNo',
    'union',
    'proofWatchman',
    'proofHoldingTax',
    'proofRent',
    'proofUtility',
    'proofOther',
    'representativeName',
    'representativeFather',
    'authorizationDate',
    'representativeAddress',
    'notIncludedReason',
    'stayYears',
    'stayMonths',
]);

const toAscii = (value) => String(value ?? '')
    .replace(/[০-৯]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0x09e6 + 48));

/** Days in the month before `date`'s month, for borrowing in the age sum. */
const daysInPreviousMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 0).getDate();

/**
 * Age on 1 January of `rollYear`, as the form writes it: whole years, then the
 * months and days left over. Null when either input is missing or the person
 * was not born yet on that day.
 *
 * @param {Date | null} birth
 * @param {string} rollYear Year digits, Bengali or ASCII.
 * @returns {{ years: number, months: number, days: number } | null}
 */
export function ageOnRollDate(birth, rollYear) {
    if (!(birth instanceof Date) || Number.isNaN(birth.getTime())) return null;
    const year = Number(toAscii(rollYear));
    if (!Number.isInteger(year) || year < 1900 || year > 2200) return null;
    const on = new Date(year, 0, 1);
    if (birth > on) return null;

    let years = on.getFullYear() - birth.getFullYear();
    let months = on.getMonth() - birth.getMonth();
    let days = on.getDate() - birth.getDate();
    if (days < 0) {
        months -= 1;
        days += daysInPreviousMonth(on);
    }
    if (months < 0) {
        years -= 1;
        months += 12;
    }
    return { years, months, days };
}

const FORM_SNAPSHOT_KEY = 'voter-inclusion-form-last-snapshot';

/** @returns {typeof INITIAL_VOTER_INCLUSION_FORM | null} */
export function getVoterInclusionFormSnapshot() {
    if (typeof window === 'undefined') return null;
    try {
        const raw = localStorage.getItem(FORM_SNAPSHOT_KEY);
        if (!raw) return null;
        return { ...INITIAL_VOTER_INCLUSION_FORM, ...JSON.parse(raw) };
    } catch {
        return null;
    }
}

export function saveVoterInclusionFormSnapshot(form) {
    if (typeof window === 'undefined' || !form) return;
    try {
        const payload = {};
        for (const key of Object.keys(INITIAL_VOTER_INCLUSION_FORM)) {
            payload[key] = displayField(form[key]);
        }
        localStorage.setItem(FORM_SNAPSHOT_KEY, JSON.stringify(payload));
    } catch {
        /* ignore */
    }
}

export function getMobileValidationError(value, t) {
    const digits = normalizeBdPhone(value);
    if (!digits) return '';
    if (digits.length < 11) {
        return t('voterInclusionForm.validation.mobileLength', { count: digits.length });
    }
    if (!isValidBdPhone(digits)) return t('voterInclusionForm.validation.mobileInvalid');
    return '';
}

/**
 * Every blank still empty, named the way the form asks for it — what the
 * download confirmation lists back. The optional lines are left out: an empty
 * ক্ষমতাপত্র is the normal case, not something forgotten.
 *
 * @param {typeof INITIAL_VOTER_INCLUSION_FORM} form
 * @param {(key: string, opts?: object) => string} t
 */
export function getEmptyVoterInclusionFields(form, t) {
    return Object.keys(INITIAL_VOTER_INCLUSION_FORM)
        .filter((key) => !OPTIONAL_KEYS.has(key) && !displayField(form?.[key]))
        .map((key) => t(`voterInclusionForm.fields.${key}`));
}

export function isVoterInclusionFormComplete(form, t) {
    return getEmptyVoterInclusionFields(form, t).length === 0
        && !getMobileValidationError(form?.mobile, t);
}

/** Everything the preview prints, already trimmed, plus the worked-out values. */
export function buildVoterInclusionForm(form) {
    const out = {};
    for (const key of Object.keys(INITIAL_VOTER_INCLUSION_FORM)) {
        out[key] = displayField(form?.[key]);
    }

    const office = [out.officeUpazila, out.officeDistrict].filter(Boolean).join(', ');
    out.officeLine = office ? `${office}।` : '';

    // The form prints "২০" and leaves two blanks for the rest of the year.
    const year = toBengaliDigits(out.ageYear);
    out.ageYearTail = year.startsWith('২০') ? year.slice(2) : year;

    const age = ageOnRollDate(parseDateField(out.birthDate), out.ageYear);
    out.ageYears = age ? toBengaliDigits(age.years) : '';
    out.ageMonths = age ? toBengaliDigits(age.months) : '';
    out.ageDays = age ? toBengaliDigits(age.days) : '';
    return out;
}
